import React, { useState, useMemo } from 'react';
import classNames from 'classnames';

import { getEmojiDataForCode, getSkinToneCodes } from '../emojiPopupUtil';

import { SKIN_TONE_EMOJI_CODE } from '../emojiPopupConstants';

import Button from '../../../buttons/Button';
import PopupPanelMouseManager from '../../../popupPanel/PopupPanelMouseManager';
import EmojiPopupEmojiButton from './EmojiPopupEmojiButton';
import { Emoji } from '../../emojiTypes';

import './EmojiPopupSkinTonePicker.scss';
import { useTranslator } from "../../../i18n/useTranslator";

type EmojiPopupSkinTonePickerProps = {
    skinTone: string;
    gridSize: number;
    emojiData: Emoji[];
    setSkinTone: (skinTone: string) => void;
};

const EmojiPopupSkinTonePicker = ({ emojiData, skinTone, setSkinTone, gridSize }: EmojiPopupSkinTonePickerProps) => {
    const [isSelectingTone, setIsSelectingTone] = useState(false);
    const translate = useTranslator('popup', 'emoji.skinTonePicker');

    const skinToneEmoji = useMemo(() => getEmojiDataForCode(emojiData, SKIN_TONE_EMOJI_CODE), [emojiData]);

    const selectSkinTone = (skinToneCode: string) => {
        setIsSelectingTone(!isSelectingTone);
        setSkinTone(skinToneCode);
    };

    const skinToneCodes = useMemo(() => getSkinToneCodes(emojiData), [emojiData]);

    if (!skinToneEmoji) return null;

    return (
        <PopupPanelMouseManager
            closePopup={() => setIsSelectingTone(false)}
            visible={isSelectingTone}
            ignoreClickContainment
        >
            <div className={classNames('EmojiPopupSkinTonePicker', { closed: !isSelectingTone })}>
                <ul className="tone-list">
                    {[null, ...skinToneCodes].map((skinToneCode, index) => (
                        <li
                            key={skinToneCode}
                            style={{
                                transform: `translate(${isSelectingTone ? index * gridSize * 3.6 : 0}px, 0px)`,
                                opacity: isSelectingTone || skinTone === skinToneCode ? 1 : 0,
                            }}
                        >
                            <EmojiPopupEmojiButton
                                onClickFn={() => selectSkinTone(skinToneCode)}
                                emoji={skinToneEmoji}
                                skinTone={skinToneCode}
                                gridSize={gridSize}
                            />
                        </li>
                    ))}
                </ul>

                <Button
                    className={classNames('select-tone-button', { hidden: isSelectingTone })}
                    onClickFn={() => setIsSelectingTone(true)}
                >
                    {translate('changeSkinTone')}
                </Button>
            </div>
        </PopupPanelMouseManager>
    );
};

export default EmojiPopupSkinTonePicker;
